import { http } from '@/api/http';

import type {
  FhirIdentifierMappingRequest,
  FhirIdentifierMappingDto,
  FhirMappedFieldsDto,
  UpdateFhirIdentifierMappingRequest,
  UpdateMappedFieldsRequest,
} from './types';

/**
 * Raw transport surface for a FHIR integration entry's child config — its identifier mappings and
 * its mapped-field allow-list. Both are scoped to a single integration entry (PTV2-1473): the backend
 * routes are nested under `/fhir-integrations/{integrationGuid}/…`, matching the `fhir_integration_id`
 * FK the two mapping tables carry. View-model orchestration (React Query hooks, mappers) lives in
 * `./api.ts`.
 *
 * The system-account credentials are no longer a child resource — since the entry-based migration
 * (PTV2-1434) they live on the integration entry itself and are edited via the entry form, so there is
 * no `/system-account` endpoint here.
 *
 * Paths are base-relative — `http` prepends `VITE_API_BASE_URL` which already carries `/api/v1`, so
 * these read as `/fhir-integrations/...`.
 */

const BASE = '/fhir-integrations';

function enc(value: string): string {
  return encodeURIComponent(value);
}

function mappingsBase(integrationGuid: string): string {
  return `${BASE}/${enc(integrationGuid)}/identifier-mappings`;
}

export const adminFhirIntegrationsEndpoints = {
  /** GET /fhir-integrations/{integrationGuid}/identifier-mappings — all mappings for the entry. */
  getFhirIdentifierMappings: (
    integrationGuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<FhirIdentifierMappingDto[]> =>
    http.get<FhirIdentifierMappingDto[]>(mappingsBase(integrationGuid), options),

  /** POST /fhir-integrations/{integrationGuid}/identifier-mappings — 201 with the created row. */
  createFhirIdentifierMapping: (
    integrationGuid: string,
    body: FhirIdentifierMappingRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FhirIdentifierMappingDto> =>
    http.post<FhirIdentifierMappingDto>(mappingsBase(integrationGuid), body, options),

  /** PUT /fhir-integrations/{integrationGuid}/identifier-mappings/{guid} — partial update. */
  updateFhirIdentifierMapping: (
    integrationGuid: string,
    guid: string,
    body: UpdateFhirIdentifierMappingRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FhirIdentifierMappingDto> =>
    http.put<FhirIdentifierMappingDto>(`${mappingsBase(integrationGuid)}/${enc(guid)}`, body, options),

  /** DELETE /fhir-integrations/{integrationGuid}/identifier-mappings/{guid} — 204. */
  deleteFhirIdentifierMapping: (
    integrationGuid: string,
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(`${mappingsBase(integrationGuid)}/${enc(guid)}`, options),

  /** GET /fhir-integrations/{integrationGuid}/mapped-fields — enabled set + available catalog. */
  getFhirMappedFields: (
    integrationGuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<FhirMappedFieldsDto> =>
    http.get<FhirMappedFieldsDto>(`${BASE}/${enc(integrationGuid)}/mapped-fields`, options),

  /** PUT /fhir-integrations/{integrationGuid}/mapped-fields — replace the allow-list; returns new state. */
  updateFhirMappedFields: (
    integrationGuid: string,
    body: UpdateMappedFieldsRequest,
    options?: { signal?: AbortSignal },
  ): Promise<FhirMappedFieldsDto> =>
    http.put<FhirMappedFieldsDto>(`${BASE}/${enc(integrationGuid)}/mapped-fields`, body, options),
};
